import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAnonymiseCandidate } from '@/hooks/use-candidates';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

const CONFIRM_PHRASE = 'anonymise';

interface CandidateGdprAnonymiseDialogProps {
  candidateId: string;
  candidateName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * HW-1055: opened right after a revoke — asks "now?" and lets the recruiter
   * put it off ("Later"); the list then keeps the candidate highlighted.
   */
  afterRevoke?: boolean;
}

/**
 * GDPR Art. 17 erasure, in two steps: what goes, then typing the phrase.
 * HW-1055: offered from the status card once consent is revoked, and right
 * after the revoke itself.
 */
export function CandidateGdprAnonymiseDialog({
  candidateId,
  candidateName,
  open,
  onOpenChange,
  afterRevoke = false,
}: CandidateGdprAnonymiseDialogProps) {
  const [step, setStep] = useState<'first' | 'second'>('first');
  const [phrase, setPhrase] = useState('');
  const anonymise = useAnonymiseCandidate(candidateId);
  const navigate = useNavigate();

  const close = () => {
    onOpenChange(false);
    setStep('first');
    setPhrase('');
  };

  const handleConfirm = async () => {
    await anonymise.mutateAsync();
    close();
    navigate({ to: '/recruitment/candidates' });
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && close()}>
      <AlertDialogContent>
        {step === 'first' ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="wrap-anywhere">
                {afterRevoke ? `Anonymise ${candidateName} now?` : `Anonymise ${candidateName}?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {afterRevoke
                  ? 'Consent is revoked, so their data has to be erased — now, or within a month at most. '
                  : ''}
                {/* HW-1056: erasure deletes the candidate, not just their contacts. */}
                The candidate and everything about them is deleted: profile and contacts, notes,
                files, skills, tags, interviews, vacancy history, consent history and career-site
                applications. Only a record that their data was erased stays. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{afterRevoke ? 'Later' : 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  setStep('second');
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {afterRevoke ? 'Anonymise' : 'Continue'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Final confirmation</AlertDialogTitle>
              <AlertDialogDescription>
                Type <span className="font-mono font-semibold">{CONFIRM_PHRASE}</span> below to
                confirm. You will be redirected to the candidate directory after completion.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-1">
              <Label htmlFor="anonymise-phrase">Confirmation</Label>
              <Input
                id="anonymise-phrase"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                autoFocus
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={anonymise.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  void handleConfirm();
                }}
                disabled={phrase !== CONFIRM_PHRASE || anonymise.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {anonymise.isPending ? 'Anonymising…' : 'Anonymise permanently'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
